<script setup>
import { ref, computed } from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import CheckInventoryContent from '@/components/Inventory/CheckInventoryContent.vue';
import QzSettingsPanel from '@/components/Inventory/QzSettingsPanel.vue';

const toast = useToast();

const ciToken = ref(localStorage.getItem('ciToken') || null);
const ciBranchId = ref(localStorage.getItem('ciBranchId') || null);
const isLoggedIn = computed(() => !!ciToken.value);
const loginEmail = ref('');
const loginPin = ref('');
const isLoggingIn = ref(false);

const doLogin = async () => {
  const email = loginEmail.value.trim();
  const pin = loginPin.value.trim();

  if (!email || !pin) {
    toast.error('Please enter email and password');
    return;
  }

  isLoggingIn.value = true;
  try {
    const resp = await axios.post('/api/Users/CheckInventoryLogin', {
      email,
      pin,
    });

    const { jwt, branchId, role, success, errors } = resp.data || {};

    if (!success) {
      toast.error(errors?.[0] || 'Login failed');
      return;
    }

    if (role !== 'Cashier' && role !== 'Manager') {
      toast.error('You are not authorized to use this tool');
      return;
    }

    ciToken.value = jwt;
    ciBranchId.value = branchId;
    localStorage.setItem('ciToken', jwt);
    localStorage.setItem('ciBranchId', branchId);
    toast.success('Logged in');
  } catch (err) {
    console.error('Login failed:', err);
    toast.error('Login failed');
  } finally {
    isLoggingIn.value = false;
  }
};

const showSettings = ref(false);

const doLogout = () => {
  ciToken.value = null;
  ciBranchId.value = null;
  localStorage.removeItem('ciToken');
  localStorage.removeItem('ciBranchId');
};
</script>

<template>
  <!-- Login Screen -->
  <div v-if="!isLoggedIn" class="min-h-screen bg-gray-50 flex items-center justify-center">
    <div class="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
      <h1 class="text-2xl font-semibold mb-6 text-center">Check Inventory</h1>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            v-model="loginEmail"
            type="email"
            autocomplete="email"
            placeholder="Email address"
            class="w-full bg-gray-100 border-0 outline-none p-3 rounded-lg"
            @keydown.enter="doLogin"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            v-model="loginPin"
            type="password"
            autocomplete="current-password"
            placeholder="PIN"
            class="w-full bg-gray-100 border-0 outline-none p-3 rounded-lg"
            @keydown.enter="doLogin"
          />
        </div>

        <button
          @click="doLogin"
          :disabled="isLoggingIn"
          class="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition"
        >
          <span v-if="isLoggingIn">Logging in...</span>
          <span v-else>Login</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Inventory Screen -->
  <div v-else class="min-h-screen bg-gray-50">
    <div class="bg-white shadow px-6 py-4 flex items-center justify-between">
      <h1 class="font-semibold text-xl">Check Inventory</h1>
      <div class="flex items-center gap-3">
        <button @click="showSettings = true" class="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <i class="pi pi-cog"></i> Settings
        </button>
        <button @click="doLogout" class="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <i class="pi pi-sign-out"></i> Logout
        </button>
      </div>
    </div>

    <QzSettingsPanel v-model:visible="showSettings" />

    <div class="px-6 py-4">
      <CheckInventoryContent :token="ciToken" :branch-id="ciBranchId" />
    </div>
  </div>
</template>
